import { ApolloClient, useApolloClient } from '@apollo/client';
import { getFragmentName, subscribe as subscribeBase, TConnectionItem } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { useEffect } from 'react';
import { EmployeeFieldsWithDependenciesFragmentDoc, onSubscriptionData } from '../../config/apollo/cache';
import {
  EmployeeFieldsFragmentDoc,
  GetEmployeeDocument,
  OnCompanyEmployeeChangedDocument,
  TGetEmployeeQuery,
  TGetEmployeeQueryVariables,
  TListCompanyEmployeesQuery,
  TListCompanyEmployeesQueryVariables,
  TOnCompanyEmployeeChangedSubscription,
  TOnCompanyEmployeeChangedSubscriptionVariables,
  useGetEmployeeLazyQuery,
  useListCompanyEmployeesLazyQuery
} from '../../generated/graphql';
import { useFragmentOrFetch } from '../shared/useFragmentOrFetch';
import { useQueryAll } from '../shared/useQueryAll';

type TOnEmployeeSubscriptionDataProps = {
  client: ApolloClient<object>;
  data?: TOnCompanyEmployeeChangedSubscription;
  vars?: TOnCompanyEmployeeChangedSubscriptionVariables;
};

const onEmployeeSubscriptionData = async (props: TOnEmployeeSubscriptionDataProps) => {
  const { client, data, vars } = props;

  const { companyId, id, _deleted } = data?.onCompanyEmployeeChanged || {};
  if (_deleted) return onSubscriptionData(data as Record<string, any>, vars);
  if (!companyId || !id) return;

  const response = await client.query<TGetEmployeeQuery, TGetEmployeeQueryVariables>({
    query: GetEmployeeDocument,
    variables: {
      id,
      companyId
    }
  });

  const employeeData = response.data.getEmployee as TConnectionItem;

  if (employeeData)
    return onSubscriptionData(
      {
        onCompanyEmployeeChanged: employeeData
      },
      vars
    );
};

export const useSubscribeToCompanyEmployeeChanged = (variables: TOnCompanyEmployeeChangedSubscriptionVariables) => {
  const client = useApolloClient();

  useEffect(() => {
    subscribeBase({
      client,
      query: OnCompanyEmployeeChangedDocument,
      variables,
      async onSubscriptionData(data, vars) {
        await onEmployeeSubscriptionData({
          client,
          data: data as TOnCompanyEmployeeChangedSubscription,
          vars: vars as TOnCompanyEmployeeChangedSubscriptionVariables
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, JSON.stringify(variables)]);
};

export type TGetEmployee = Exclude<TGetEmployeeQuery['getEmployee'], undefined | null>;

export const useFragmentOrFetchEmployee = (variables: TGetEmployeeQueryVariables) => {
  useSubscribeToCompanyEmployeeChanged({
    companyId: variables.companyId
  });

  return useFragmentOrFetch<TGetEmployee, TGetEmployeeQuery, TGetEmployeeQueryVariables>({
    fragmentDoc: EmployeeFieldsWithDependenciesFragmentDoc,
    fragmentName: getFragmentName(EmployeeFieldsFragmentDoc),
    useLazyQuery: useGetEmployeeLazyQuery,
    variables,
    __typename: 'Employee'
  });
};

export type TListEmployee = Exclude<TListCompanyEmployeesQuery['listCompanyEmployees']['items'][number], undefined | null>;

export const useQueryAllEmployees = (variables: TListCompanyEmployeesQueryVariables) => {
  useSubscribeToCompanyEmployeeChanged({
    companyId: variables.companyId
  });

  return useQueryAll<TListCompanyEmployeesQuery, TListCompanyEmployeesQueryVariables>({
    useQuery: useListCompanyEmployeesLazyQuery,
    variables
  });
};
